import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { TimeoutError, catchError, finalize, throwError, timeout } from 'rxjs';
import { HttpDiagnosticsService } from '../services/http-diagnostics.service';

/**
 * Endpoints operativos que deben responder rapido (cola de cocina/barra, pedidos,
 * caja). Si uno se cuelga y no hay timeout, retiene una de las ~6 conexiones HTTP
 * que el navegador permite por dominio. Bajo alto volumen de pedidos esas 6 se
 * llenan, los requests nuevos quedan encolados para siempre (incluido el polling
 * y, si SignalR cae a long-polling, sus eventos) y la pantalla se "congela" entera.
 * El unico reset era recargar. Con timeout, una conexion colgada se aborta y se
 * libera sola.
 */
const OPERATIONAL_PATH_FRAGMENTS = ['/kitchen/', '/orders', '/cashregister', '/bar-items'];

/** Generoso: lo normal es <2s. 15s solo corta lo que realmente se colgo. */
const OPERATIONAL_TIMEOUT_MS = 15_000;

const KITCHEN_STATUS_PUT = /\/kitchen\/items\/[^/]+\/status$/;

/**
 * Solo aplicamos timeout a lo idempotente/seguro de reabortar:
 *  - cualquier GET operativo (leer es siempre seguro de reintentar).
 *  - el PUT de estado de cocina (marcar Preparing/Ready N veces == 1 vez).
 * NO tocamos POST/PATCH/DELETE (pagos, crear pedido): un POST lento pero exitoso
 * que expire podria inducir un reintento manual y duplicar el cobro.
 */
function shouldTimeout(method: string, url: string): boolean {
  if (!OPERATIONAL_PATH_FRAGMENTS.some(fragment => url.includes(fragment))) {
    return false;
  }
  if (method === 'GET') {
    return true;
  }
  return method === 'PUT' && KITCHEN_STATUS_PUT.test(url);
}

export const operationalTimeoutInterceptor: HttpInterceptorFn = (req, next) => {
  const diagnostics = inject(HttpDiagnosticsService);
  diagnostics.increment();

  let stream = next(req);

  if (shouldTimeout(req.method, req.url)) {
    stream = stream.pipe(
      timeout(OPERATIONAL_TIMEOUT_MS),
      catchError((err: unknown) => {
        if (err instanceof TimeoutError) {
          diagnostics.recordTimeout(req.url);
          return throwError(() => new Error('La solicitud tardó demasiado y se canceló. Reintenta.'));
        }
        return throwError(() => err);
      })
    );
  }

  // finalize cubre exito, error, timeout y abort -> el contador nunca se desbalancea.
  return stream.pipe(finalize(() => diagnostics.decrement()));
};
